import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { Show } from '../../components/shared/Show';
import { isUndefined } from 'lodash';

interface TabListTWProps {
  /* The titles for each tab in the tab list, this will dictate how many tabs exist */
  tabTitles: string[];
  /* Map containing the tab content for each tab title - if no content is specified for a tab, an empty element will render in place */
  tabContent?: Record<string, ReactNode>;
  /* Default selected tab - this tabs content and selected styling will render on initial load */
  defaultTab?: string;
  /* tailwind class strings to add any additional styling to the tab elements */
  className?: string;
}

export const TabListTW: React.FC<TabListTWProps> = ({ tabTitles, tabContent = {}, defaultTab, className }) => {
  const [selectedTab, setSelectedTab] = useState<string | undefined>(defaultTab);

  const hasTabContent = !isUndefined(selectedTab) && tabContent.hasOwnProperty(selectedTab);
  return (
    <>
      <div className={classNames('inline-flex h-min w-full items-center justify-start rounded-lg shadow')}>
        {tabTitles.map((title) => {
          return (
            <TabTW
              key={title}
              tabTitle={title}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              className={className}
            />
          );
        })}
      </div>
      <div>
        <Show when={hasTabContent}>
          <>{tabContent![selectedTab!]}</>
        </Show>
      </div>
    </>
  );
};

interface TabTWProps {
  /* The title text to display in the tab */
  tabTitle: string;
  /* A callback triggered when a new tab is clicked */
  onClick?: () => void;
  /* Holds the name of the currently selected tab */
  selectedTab?: string;
  /* Helper function intended to be used by TabListTW to manage the current active tab styling */
  setSelectedTab: (selected: string) => void;
  /* tailwind class strings to add any additional styling to the tab elements */
  className?: string;
}

export const TabTW: React.FC<TabTWProps> = ({ tabTitle, onClick, selectedTab, setSelectedTab, className }) => {
  const isSelected = selectedTab === tabTitle;
  return (
    <div
      className={classNames(
        'inline-flex shrink grow basis-0 cursor-pointer flex-col items-center justify-center rounded-md bg-white shadow-sm'
      )}
      onClick={() => {
        setSelectedTab(tabTitle);
        if (onClick) {
          onClick();
        }
      }}
    >
      <div className={classNames('inline-flex items-center justify-center px-4 pb-3.5 pt-4', className)}>
        <div
          className={classNames(
            (isSelected ? 'text-gray-900' : 'text-gray-500') + ' text-sm font-medium leading-tight'
          )}
        >
          {tabTitle}
        </div>
      </div>
      <div className={classNames('h-0.5 self-stretch ' + (isSelected ? 'animate-pulse-short bg-emerald-500' : ''))} />
    </div>
  );
};
